/**
 * UcsIcon2D — the origin UCS icon (Faz 11.10, OCS UcsIconParams parity).
 *
 * A two-axis L shape at the world origin: the X arm inks in the axis red,
 * the Y arm in the axis green, with the classic "W" tag while the world UCS
 * is active. Screen-fixed size: the arm length and the label size are
 * re-derived per frame from pixelsToWorld behind an epsilon guard, so
 * pan/zoom never rescales the icon (the classic drafting behavior — the
 * icon rides the origin, not the zoom).
 *
 * UCSICON command parity: 'off' hides it, 'on'/'origin' render it (yap
 * draws the origin icon; there is no corner variant to mirror).
 */

import { useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Line, Text } from '@react-three/drei';
import { useViewStore } from '../state/commandAuxStores';
import { pixelsToWorld } from '../tools/snapping/SnapManager';
import { AXIS_COLORS } from '../themes/colors';

/** Arm length and label height in screen pixels. */
const ARM_PX = 22;
const LABEL_PX = 8;
/** Re-render threshold for zoom-driven size changes (relative fraction). */
const SIZE_EPSILON = 0.08;

export function UcsIcon2D() {
    const ucsIcon = useViewStore((s) => s.ucsIcon);
    const { camera, gl } = useThree();
    const groupRef = useRef<THREE.Group>(null);
    const [arm, setArm] = useState(1);
    const [labelSize, setLabelSize] = useState(0.3);

    const origin = useMemo(() => new THREE.Vector3(0, 0, 0.02), []);

    useFrame(() => {
        if (ucsIcon === 'off') return;
        const nextArm = pixelsToWorld(ARM_PX, origin, camera, gl);
        setArm((prev) => (
            Math.abs(prev - nextArm) / Math.max(nextArm, 1e-6) > SIZE_EPSILON ? nextArm : prev
        ));
        const nextLabel = pixelsToWorld(LABEL_PX, origin, camera, gl);
        setLabelSize((prev) => (
            Math.abs(prev - nextLabel) / Math.max(nextLabel, 1e-6) > SIZE_EPSILON ? nextLabel : prev
        ));
    });

    if (ucsIcon === 'off') return null;

    // World UCS: the Y arm carries the classic "W" tag at its tip.
    const labelAt: [number, number, number] = [arm * 1.35, arm * 1.35, 0];

    return (
        <group ref={groupRef} position={origin} renderOrder={7}>
            {/* X arm (world +X) */}
            <Line
                points={[[0, 0, 0], [arm, 0, 0]]}
                color={AXIS_COLORS.x}
                lineWidth={2}
            />
            {/* Y arm (world +Y) */}
            <Line
                points={[[0, 0, 0], [0, arm, 0]]}
                color={AXIS_COLORS.y}
                lineWidth={2}
            />
            <Text
                position={labelAt}
                fontSize={labelSize}
                color={AXIS_COLORS.y}
                anchorX="left"
                anchorY="middle"
                outlineWidth={labelSize * 0.08}
                outlineColor="#000000"
            >
                W
            </Text>
        </group>
    );
}

export default UcsIcon2D;
